// client/src/pages/SearchPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import SearchFilters from '../components/SearchFilters';
import CourseList from '../components/CourseList';
import { fetchSearchResults } from '../services/api';

const SearchPage = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // ✅ State for filters is now derived directly from the URL for a single source of truth.
  const [clientFilters, setClientFilters] = useState({
    startTime: 6,
    endTime: 21,
    golfers: 4,
    holes: 18,
    // Radius is now read from searchParams, with a default.
    radius: searchParams.get('radius') || '10', 
  });

  // ✅ useCallback prevents this function from being recreated on every render.
  const fetchData = useCallback(async (params) => {
    // Only fetch if we have a location or a search query.
    if (!params.get('searchQuery') && !params.get('lat')) return;
    
    setIsLoading(true);
    try {
      const results = await fetchSearchResults(params);
      setSearchResults(results);
    } catch (err) {
      console.error("Error fetching search results:", err);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ HOOK 1: Runs ONLY ONCE on initial page load to get location.
  useEffect(() => {
    // If the URL already has location data, don't do anything.
    if (searchParams.get('lat') || searchParams.get('searchQuery')) {
      fetchData(searchParams);
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newParams = new URLSearchParams({
          date: today,
          lat: latitude,
          lon: longitude,
          radius: clientFilters.radius,
        });
        setSearchParams(newParams, { replace: true });
      },
      (error) => {
        console.error("Geolocation error:", error);
        const newParams = new URLSearchParams({
          date: today,
          searchQuery: 'Austin',
          searchType: 'city',
          radius: clientFilters.radius,
        });
        setSearchParams(newParams, { replace: true });
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // <-- Empty dependency array means this runs only once.

  // ✅ HOOK 2: Runs whenever the URL's search parameters change.
  useEffect(() => {
    fetchData(searchParams);
  }, [searchParams, fetchData]);

  // ✅ NEW: This function now ONLY updates the URL. The effect above will handle the refetch.
  const handleFilterChange = (newFilters) => {
    setClientFilters(newFilters);
    
    // Update the radius in the URL without triggering a full page reload.
    const newParams = new URLSearchParams(searchParams);
    newParams.set('radius', newFilters.radius);
    setSearchParams(newParams, { replace: true });
  };
  
  return (
    <Container>
      <Row>
        <Col md={3} className="ps-0">
          <SearchFilters
            filters={clientFilters}
            setFilters={handleFilterChange} // Use the new handler
            courseCount={searchResults.length}
          />
        </Col>
        <Col md={9}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading courses...</span>
              </Spinner>
            </div>
          ) : (
            <CourseList
              searchResults={searchResults}
              date={searchParams.get('date')}
              filters={clientFilters}
              isLoading={isLoading}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SearchPage;